import React, { useState } from "react";
import { JetSportBox } from "../../components/BoxView/JetSportBox";
import { BoxHeader } from "../../components/BoxView/BoxHeader";
import { BoxContent } from "../../components/BoxView/BoxContent";
import { PrimaryButton } from "../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../components/Buttons/SecondaryButton";
import { CustomInput } from "../../components/Input/CustomInput";
import { InputLabel } from "../../components/Input/InputLabel";
import { BoxHeaderTypography } from "../../components/Typographies/BoxHeaderTypography";
import { calculate, LcaPayload, LcaResult } from "../../../utils/lca";

type ProcessType = "Broderi" | "Direkte Tryk" | "Transfer" | "Digital Transfer";

const PROCESS_TYPES: ProcessType[] = [
  "Broderi",
  "Direkte Tryk",
  "Transfer",
  "Digital Transfer",
];

function formatValue(x: number): string {
  if (x >= 100) return x.toFixed(2);
  if (x >= 1) return x.toFixed(4);
  return x.toExponential(4);
}

export function Co2CalculatorScreen() {
  const [processType, setProcessType] = useState<ProcessType>("Transfer");
  const [sizeCm2, setSizeCm2] = useState("201.6");
  const [amount, setAmount] = useState("100");

  // Broderi
  const [stitchesPerCm2, setStitchesPerCm2] = useState("3");
  const [avalon, setAvalon] = useState(false);

  // Direkte Tryk
  const [dtColorCount, setDtColorCount] = useState("3");
  const [darkTextile, setDarkTextile] = useState(false);

  // Transfer / Digital Transfer
  const [trColorCount, setTrColorCount] = useState("3");
  const [logosPerSheet, setLogosPerSheet] = useState("6");
  const [white, setWhite] = useState(false);
  const [blokker, setBlokker] = useState(false);
  const [lim, setLim] = useState(false);

  const [result, setResult] = useState<LcaResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<{
    message: string;
    success: boolean;
  } | null>(null);

  function buildPayload(): LcaPayload {
    const base = {
      Amount: Number(amount),
      SizeCM2: Number(sizeCm2),
    };

    switch (processType) {
      case "Broderi":
        return {
          ...base,
          Type: "Broderi",
          "Number of stitches per cm2": Number(stitchesPerCm2),
          Avalon: avalon,
        };
      case "Direkte Tryk":
        return {
          ...base,
          Type: "Direkte Tryk",
          ColorCount: Number(dtColorCount),
          DarkTextile: darkTextile,
        };
      case "Transfer":
        return {
          ...base,
          Type: "Transfer",
          ColorCount: Number(trColorCount),
          White: white,
          Blokker: blokker,
          Lim: lim,
          "Logos per sheet": Number(logosPerSheet),
        };
      case "Digital Transfer":
        return {
          ...base,
          Type: "Digital Transfer",
          ColorCount: Number(trColorCount),
          White: white,
          Blokker: blokker,
          Lim: lim,
          "Logos per sheet": Number(logosPerSheet),
        };
    }
  }

  function handleCalculate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const payload = buildPayload();
      const r = calculate(payload);
      setResult(r);
    } catch (err) {
      setResult(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleRunTests() {
    setTestStatus({ message: "Fetching test data...", success: true });
    try {
      const res = await fetch("/test_cases.json");
      const cases = await res.json();
      let fails = 0;
      for (const c of cases) {
        const got = calculate(c.input);
        if (
          Math.abs(got.VuggeTilPortPerLogo - c.VuggeTilPortPerLogo) > 1e-9
        ) {
          fails++;
          console.error("FAIL", c.input, got, "want", c.VuggeTilPortPerLogo);
        }
      }
      if (fails === 0) {
        setTestStatus({
          message: `All ${cases.length} tests passed`,
          success: true,
        });
      } else {
        setTestStatus({
          message: `${fails}/${cases.length} tests failed (see console)`,
          success: false,
        });
      }
    } catch (err) {
      setTestStatus({
        message: `Could not run tests: ${err instanceof Error ? err.message : String(err)}`,
        success: false,
      });
    }
  }

  const isBroderi = processType === "Broderi";
  const isDirekteTryk = processType === "Direkte Tryk";
  const isTransferType =
    processType === "Transfer" || processType === "Digital Transfer";

  return (
    <div className="max-w-[90rem] mx-auto px-6 lg:px-10 py-10">
      <JetSportBox>
        <BoxHeader>
          <BoxHeaderTypography>CO2e Calculator</BoxHeaderTypography>
          <p className="text-xs text-gray-600 mt-1">
            Cradle-to-gate (vugge-til-port) GWP-fossil per logo — matches the
            certified Excel 1:1
          </p>
        </BoxHeader>

        <BoxContent className="!min-h-0 pb-8">
          <form onSubmit={handleCalculate}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
              {/* Process type */}
              <div className="md:col-span-2 space-y-0.5 pb-3">
                <InputLabel>Process</InputLabel>
                <select
                  value={processType}
                  onChange={(e) =>
                    setProcessType(e.target.value as ProcessType)
                  }
                  className="input bg-gray-100 border-gray-200 h-9 px-3 w-full"
                >
                  {PROCESS_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              {/* Common fields */}
              <div className="space-y-0.5 pb-3">
                <InputLabel>Print size [cm2]</InputLabel>
                <CustomInput
                  type="number"
                  value={sizeCm2}
                  onChange={(e) => setSizeCm2(e.target.value)}
                  step="0.1"
                  min="0"
                  className="w-full"
                />
              </div>
              <div className="space-y-0.5 pb-3">
                <InputLabel>Number of logos in order</InputLabel>
                <CustomInput
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  step="1"
                  min="1"
                  className="w-full"
                />
              </div>

              {/* Broderi fields */}
              {isBroderi && (
                <>
                  <div className="space-y-0.5 pb-3">
                    <InputLabel>Stitches per cm2</InputLabel>
                    <CustomInput
                      type="number"
                      value={stitchesPerCm2}
                      onChange={(e) => setStitchesPerCm2(e.target.value)}
                      step="1"
                      min="0"
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-0.5 pb-3">
                    <InputLabel>Avalon (water-soluble stabilizer)</InputLabel>
                    <label className="flex items-center gap-2 input bg-gray-100 border-gray-200 h-9 px-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={avalon}
                        onChange={(e) => setAvalon(e.target.checked)}
                      />
                      <span className="text-xs">Applied</span>
                    </label>
                  </div>
                </>
              )}

              {/* Direkte Tryk fields */}
              {isDirekteTryk && (
                <>
                  <div className="space-y-0.5 pb-3">
                    <InputLabel>Number of colors</InputLabel>
                    <CustomInput
                      type="number"
                      value={dtColorCount}
                      onChange={(e) => setDtColorCount(e.target.value)}
                      step="1"
                      min="1"
                      max="8"
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-0.5 pb-3">
                    <InputLabel>Dark textile (requires backstop)</InputLabel>
                    <label className="flex items-center gap-2 input bg-gray-100 border-gray-200 h-9 px-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={darkTextile}
                        onChange={(e) => setDarkTextile(e.target.checked)}
                      />
                      <span className="text-xs">Yes</span>
                    </label>
                  </div>
                </>
              )}

              {/* Transfer / Digital Transfer fields */}
              {isTransferType && (
                <>
                  <div className="space-y-0.5 pb-3">
                    <InputLabel>Number of colors</InputLabel>
                    <CustomInput
                      type="number"
                      value={trColorCount}
                      onChange={(e) => setTrColorCount(e.target.value)}
                      step="1"
                      min="1"
                      max="8"
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-0.5 pb-3">
                    <InputLabel>Logos per sheet</InputLabel>
                    <CustomInput
                      type="number"
                      value={logosPerSheet}
                      onChange={(e) => setLogosPerSheet(e.target.value)}
                      step="1"
                      min="1"
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-0.5 pb-3">
                    <InputLabel>White underprint</InputLabel>
                    <label className="flex items-center gap-2 input bg-gray-100 border-gray-200 h-9 px-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={white}
                        onChange={(e) => setWhite(e.target.checked)}
                      />
                      <span className="text-xs">Applied</span>
                    </label>
                  </div>
                  <div className="space-y-0.5 pb-3">
                    <InputLabel>Blokker</InputLabel>
                    <label className="flex items-center gap-2 input bg-gray-100 border-gray-200 h-9 px-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={blokker}
                        onChange={(e) => setBlokker(e.target.checked)}
                      />
                      <span className="text-xs">Applied</span>
                    </label>
                  </div>
                  <div className="space-y-0.5 pb-3">
                    <InputLabel>Lim (adhesive)</InputLabel>
                    <label className="flex items-center gap-2 input bg-gray-100 border-gray-200 h-9 px-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={lim}
                        onChange={(e) => setLim(e.target.checked)}
                      />
                      <span className="text-xs">Applied</span>
                    </label>
                  </div>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <PrimaryButton type="submit" width="sm">
                Calculate
              </PrimaryButton>
              <SecondaryButton
                type="button"
                width="sm"
                onClick={handleRunTests}
              >
                Run 40 regression tests
              </SecondaryButton>
              {testStatus && (
                <span
                  className={`text-xs ${
                    testStatus.success ? "text-success" : "text-error"
                  }`}
                >
                  {testStatus.message}
                </span>
              )}
            </div>
          </form>

          {/* Error */}
          {error && (
            <div className="mt-6 rounded-md border border-red-200 bg-red-50 px-6 py-4 text-sm text-red-800">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="mt-6 rounded-md border border-green-200 bg-green-50 px-6 py-5">
              <h2 className="text-sm font-semibold text-green-700 mb-4 uppercase">
                Result
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xxs text-gray-600 uppercase tracking-wide mb-1">
                    VuggeTilPortPerLogo
                  </div>
                  <div className="text-lg font-semibold">
                    {formatValue(result.VuggeTilPortPerLogo)}
                    <span className="text-xs text-gray-600 font-medium ml-1">
                      kg CO2-eq / logo
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xxs text-gray-600 uppercase tracking-wide mb-1">
                    VuggeTilPortTotal
                  </div>
                  <div className="text-lg font-semibold">
                    {formatValue(result.VuggeTilPortTotal)}
                    <span className="text-xs text-gray-600 font-medium ml-1">
                      kg CO2-eq total
                    </span>
                  </div>
                </div>
              </div>

              <details className="mt-4">
                <summary className="cursor-pointer text-xs text-gray-600 py-1">
                  Show JSON response
                </summary>
                <pre className="mt-2 p-3 bg-white border border-gray-200 rounded-md text-xxs font-mono overflow-x-auto text-gray-600">
                  {JSON.stringify(
                    {
                      VuggeTilPortPerLogo: result.VuggeTilPortPerLogo,
                      VuggeTilPortTotal: result.VuggeTilPortTotal,
                    },
                    null,
                    2
                  )}
                </pre>
              </details>

              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-gray-600 py-1">
                  Show cell breakdown
                </summary>
                <pre className="mt-2 p-3 bg-white border border-gray-200 rounded-md text-xxs font-mono overflow-x-auto text-gray-600">
                  {JSON.stringify(result.breakdown, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </BoxContent>
      </JetSportBox>
    </div>
  );
}
